import styled from 'styled-components';
import spacing from '@styles/spacing';
import colors, {
  colorTextInverse,
  colorTextLinkHover,
  colorTextDefault,
} from '@toolshed/styleguide/styles/colors';
import tablet from '@styles/breakpoint/tablet';
import {
  regular,
  bold,
  semibold,
} from '@toolshed/styleguide/styles/typography';
import { fontSize3, HeadingXXL, lineHeight2 } from 'lib/design-system';
import {
  default900,
  breakpointMediumStart,
  breakpointSmallEnd,
  neutral600,
  default500,
  neutral0,
  fontSizeLabelSM,
  fontSizeLabelsRegular,
  spacing1,
  spacing2,
  spacing3,
  spacing4,
  spacing6,
  spacing14,
  breakpointLargeStart,
  fontSizeBodySM,
  fontWeightRegular,
  fontWeightSemibold,
  TertiaryLoadingButton,
  borderRadius,
} from 'lib/mortar';

export const Section = styled.section<StyledSection>`
  display: inline-grid;
  grid-template-columns: ${spacing4} 1fr;
  column-gap: ${spacing1};
  width: fit-content;
  align-self: flex-start;

  @media screen and (min-width: ${breakpointMediumStart}) {
    align-self: center;
    grid-template-columns: ${spacing4} auto auto;
  }
`;

export const HeadingL = styled.p<StyledParagraph>`
  font-size: ${fontSizeLabelsRegular};
  line-height: 1.5;
  font-weight: 600;
  margin: 0;
  color: ${neutral600};

  @media screen and (min-width: ${breakpointMediumStart}) {
    &::after {
      content: ' - ';
    }
  }
`;

export const TextLink = styled.a<StyledAnchor>`
  color: ${default500};
  text-decoration: none;
  font-weight: 600;

  &:active,
  &:visited {
    color: ${default500};
  }
`;

export const BodyText = styled.p<StyledParagraph>`
  margin: 0;
  font-size: ${fontSizeLabelSM};
  line-height: 1.8;
  font-weight: 400;
  color: ${neutral600};
`;

export const InfoIcon = styled.span<StyledSpan>`
  grid-row: 1/3;
  grid-column: 1/2;
  background-image: url('/static/check_rgb_flat_ctick.svg');
  background-size: contain;
  background-position: center;
  background-repeat: no-repeat;

  @media screen and (min-width: ${breakpointMediumStart}) {
    grid-row: 1/2;
  }
`;

export const SearchPageContent = styled.div<StyledDiv>`
  width: 100%;
`;

export const Wrapper = styled.div<StyledDiv>`
  width: calc(100% - ${spacing.medium * 2}px);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  background-color: ${colors.white};
  border-radius: ${borderRadius(1)};
  box-shadow: 0px 3px 6px 0px rgba(0, 0, 0, 0.16);
  box-sizing: border-box;
  padding: ${spacing.xxlarge}px;
  margin-top: ${spacing.xxlarge}px;
  margin-bottom: ${spacing2};

  ${tablet(`
    width: 570px;
    margin: ${spacing.xxlarge}px auto ${spacing2};
  `)};
`;

export const Main = styled.div<StyledDiv>`
  width: 100%;
  display: flex;
  flex-direction: row;
`;

export const SEOBlurbRedesign = styled(Main)`
  flex-direction: column;
  box-sizing: border-box;
  max-width: var(--search-page-max-width, 1030px);
  width: calc(100% - (2 * var(--edge-spacing, ${spacing4})));
  margin: var(--blurb-top-margin, 0) var(--edge-spacing, ${spacing4}) 0;

  a {
    font-size: ${fontSize3};
    font-weight: ${fontWeightSemibold};
    color: ${colorTextLinkHover};
    text-decoration: none;

    &:hover,
    &:focus,
    &:active {
      text-decoration: underline;
    }
  }
`;

export const SEOIntroRedesign = styled.div<StyledDiv>`
  margin-bottom: ${spacing6};

  @media screen and (min-width: ${breakpointMediumStart}) {
    margin-bottom: ${spacing4};
  }
`;

export const List = styled.div<StyledDiv>`
  gap: ${spacing4};
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  margin: 0 0 ${spacing2};
  flex: 1 1 100%;
  min-width: 0; // Hack to make this the correct width

  // @TODO can I not have this be TertiaryButtonWithLoader? Is it OK like this???
  ${TertiaryLoadingButton} {
    @media (max-width: ${breakpointSmallEnd}) {
      width: 100%;
    }
  }
`;

export const Title = styled.h1<StyledHeading>`
  ${bold('lbase')};
  color: ${colors.medium};
  margin-top: ${spacing.xxlarge}px;
  margin-bottom: 0;
`;

export const Suggestion = styled.div<StyledDiv>`
  ${semibold('lbase')};
  color: ${colors.medium};
  margin-top: ${spacing.xxlarge}px;
  margin-bottom: 0;
`;

export const Description = styled.pre`
  ${regular('small')};
  color: ${colors.medium};
  text-align: center;
  margin: 0;
  padding: 0;
  margin-top: ${spacing.xxlarge}px;
`;

export const DockWrapper = styled.div<StyledDiv>`
  top: 60px;
  width: 100%;
  display: block;
`;

export const TradeSignUpBannerWrapperRedesign = styled.aside`
  font-size: ${fontSize3};
  font-weight: ${fontWeightSemibold};
  background: ${default900};
  color: ${colorTextInverse};
  display: flex;
  flex-direction: column;
  row-gap: ${spacing4};
  box-sizing: border-box;
  width: 100%;
  padding: var(--sign-up-banner-tb-padding, ${spacing4})
    var(--edge-spacing, ${spacing4});

  @media screen and (min-width: ${breakpointMediumStart}) {
    align-items: center;
    flex-direction: row;
    column-gap: ${spacing6};
    justify-content: center;
  }
`;

export const TradeSignUpTextIconWrapperRedesign = styled.div<StyledDiv>`
  overflow-wrap: break-word;
  display: flex;
  align-items: center;
  column-gap: ${spacing4};
`;

export const SearchRedesignBottomStuff = styled.div<StyledDiv>`
  margin-top: ${spacing14};
  background-color: ${neutral0};
  width: 100%;
  --edge-spacing: ${spacing4};
  --sign-up-banner-tb-padding: ${spacing4};
  --nearby-location-links-top-margin: ${spacing6};
  --blurb-top-margin: ${spacing6};
  --breadcrumb-top-margin: ${spacing6};
  --search-page-max-width: 998px;
  padding-bottom: ${spacing4};
  align-items: center;
  display: flex;
  flex-direction: column;

  @media screen and (min-width: ${breakpointMediumStart}) {
    --sign-up-banner-tb-padding: ${spacing3};
  }
`;

export const TotalResults = styled.div<StyledDiv>`
  background-color: ${neutral0};
  width: 100%;
  display: flex;
  justify-content: center;

  ${HeadingXXL} {
    font-size: ${fontSizeBodySM};
    color: ${colorTextDefault};
    max-width: var(--search-page-max-width, 1030px);
    width: calc(100% - (2 * var(--edge-spacing, ${spacing4})));
    padding: ${spacing2} 0;
    box-sizing: border-box;
    font-weight: ${fontWeightRegular};
    line-height: ${lineHeight2};

    @media screen and (min-width: ${breakpointLargeStart}) {
      padding: ${spacing3} var(--edge-spacing, ${spacing4}) ${spacing2};
    }
  }
`;

export const ConsumerGuidanceBannerWrapper = styled.div<StyledDiv>`
  background-color: ${neutral0};
  width: 100%;
  display: flex;
  justify-content: center;

  ${HeadingXXL} {
    font-size: ${fontSizeBodySM};
    color: ${colorTextDefault};
    max-width: var(--search-page-max-width, 1030px);
    width: calc(100% - (2 * var(--edge-spacing, ${spacing4})));
    padding: ${spacing2} 0;
    box-sizing: border-box;
    font-weight: ${fontWeightRegular};
    line-height: ${lineHeight2};

    @media screen and (min-width: ${breakpointLargeStart}) {
      padding: ${spacing3} var(--edge-spacing, ${spacing4}) ${spacing2};
    }
  }
`;
